// PictureTable (the price book round, DEC-019): a list to act on, one row a thing with its picture: tick
// rows to act on them together, press a name to open it beside the list. The columns after the name are
// the caller's (where it sits, the price, the member price, how many sold, its words' state); a cell
// says itself as words, muted for "none", red for a problem, dashed underline for what waits on someone
// (Jev's draft). The open row is marked with the ink rule on its left. Under 700 a row folds to its
// picture and name with its cells as lines under them.
// Why not a LineTable variant: LineTable is a record's lines, read in place (an invoice, a visit); this
// is a list you choose from and act on, with a picture, ticks, an open row and its own fold. Giving
// LineTable those would give it two jobs (HIERARCHY.md rule 1). Reads only --pictable-*; Picture,
// Checkbox read their own.
import type { ReactNode } from 'react';
import { Checkbox } from '../Checkbox/Checkbox.tsx';
import { Picture, type PictureSrc, type PictureState } from '../Picture/Picture.tsx';
import './PictureTable.css';

/** A cell: words, with how they read; or anything else drawn as it is. */
export type PictureCell = ReactNode | { text: string; tone?: 'muted' | 'bad' | 'waiting' };

export interface PictureColumn {
  id: string;
  label: string;
  /** Figures sit at the end. */
  align?: 'end';
}
export interface PictureRow {
  id: string;
  name: string;
  /** Under the name, muted: its code. */
  code?: string;
  picture?: PictureSrc | null;
  pictureState?: PictureState;
  cells: Record<string, PictureCell>;
}

export interface PictureTableProps {
  /** What the rows are, for screen readers ("Items"). */
  label: string;
  /** The first column's heading ("Item"). */
  nameLabel: string;
  columns: PictureColumn[];
  rows: PictureRow[];
  /** Ticked rows; with onSelect, every row has its tick. */
  selected?: readonly string[];
  onSelect?: (ids: string[]) => void;
  /** The row open beside the list. */
  current?: string | null;
  onOpen?: (id: string) => void;
}

/** Nothing to say: on a phone the row leaves its label out too. */
const isEmpty = (c: PictureCell) =>
  c == null ||
  c === '' ||
  (typeof c === 'object' && 'text' in (c as object) && !(c as { text: string }).text);
const isWords = (c: PictureCell): c is { text: string; tone?: 'muted' | 'bad' | 'waiting' } =>
  typeof c === 'object' && c !== null && 'text' in c && !('$$typeof' in c);

function Cell({ c }: { c: PictureCell }) {
  if (!isWords(c)) return <>{c}</>;
  return (
    <span className="fl-pictable__words" data-tone={c.tone}>
      {c.text}
    </span>
  );
}

export function PictureTable({
  label,
  nameLabel,
  columns,
  rows,
  selected = [],
  onSelect,
  current,
  onOpen,
}: PictureTableProps) {
  const on = new Set(selected);
  const all = rows.length > 0 && rows.every((r) => on.has(r.id));
  const tick = (id: string, to: boolean) => {
    const n = new Set(on);
    if (to) n.add(id);
    else n.delete(id);
    onSelect?.(rows.filter((r) => n.has(r.id)).map((r) => r.id));
  };
  return (
    <table className="fl-pictable" aria-label={label}>
      <thead>
        <tr>
          {onSelect ? (
            <th className="fl-pictable__ck" scope="col">
              <Checkbox
                isSelected={all}
                aria-label={all ? 'Clear every tick' : `Tick all ${rows.length}`}
                onChange={(v) => onSelect(v ? rows.map((r) => r.id) : [])}
              >
                {''}
              </Checkbox>
            </th>
          ) : null}
          <th className="fl-pictable__pic" scope="col">
            <span className="fl-pictable__sr">Picture</span>
          </th>
          <th scope="col">{nameLabel}</th>
          {columns.map((c) => (
            <th key={c.id} scope="col" data-align={c.align}>
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr
            key={r.id}
            data-current={current === r.id ? '' : undefined}
            data-ticked={on.has(r.id) ? '' : undefined}
          >
            {onSelect ? (
              <td className="fl-pictable__ck">
                <Checkbox
                  isSelected={on.has(r.id)}
                  aria-label={`Tick ${r.name}`}
                  onChange={(v) => tick(r.id, v)}
                >
                  {''}
                </Checkbox>
              </td>
            ) : null}
            <td className="fl-pictable__pic">
              <Picture src={r.picture} state={r.pictureState} size="sm" />
            </td>
            <th scope="row" className="fl-pictable__name">
              {onOpen ? (
                <button
                  type="button"
                  className="fl-pictable__open"
                  aria-current={current === r.id ? 'true' : undefined}
                  onClick={() => onOpen(r.id)}
                >
                  {r.name}
                </button>
              ) : (
                <span className="fl-pictable__strong">{r.name}</span>
              )}
              {r.code ? <span className="fl-pictable__code">{r.code}</span> : null}
            </th>
            {columns.map((c) => (
              <td
                key={c.id}
                data-align={c.align}
                data-label={c.label}
                data-empty={isEmpty(r.cells[c.id]) ? '' : undefined}
              >
                <Cell c={r.cells[c.id]} />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
